import styled, { css } from "styled-components/macro";

import { Title } from "shared/components";
import {
  borderRadius,
  breakpoint,
  colors,
  ContentWidth,
  fontStyle,
  space,
} from "shared/styles/constants";
import { ICardTheme } from "shared/types";

export interface CardContainerProps {
  theme?: ICardTheme;
  hideContainer?: boolean;
  size?: "s" | "m";
  width?: ContentWidth | string;
  center?: boolean;
}

const getCardThemeColor = (theme?: ICardTheme) => {
  switch (theme) {
    case ICardTheme.header:
      return colors.blackLight;
    case ICardTheme.secondary:
      return colors.grey1;
    case ICardTheme.primary:
    default:
      return colors.blackLight;
  }
};

export const CardContainer = styled.div<CardContainerProps>`
  position: relative;

  display: flex;
  flex-direction: column;

  width: ${({ width = "100%" }) => width};

  border-radius: ${borderRadius.m};
  background-color: ${({ theme }) => getCardThemeColor(theme)};

  ${({ hideContainer }) =>
    hideContainer &&
    css`
      border-radius: none;
      background-color: transparent;
    `}

  @media print {
    * {
      color: black;
    }

    background-color: transparent;
    margin: 0 !important;
    display: block;
  }
`;

interface CardHeaderProps {
  isEmpty?: boolean;
  accentColor?: string;
  center?: boolean;
}

export const CardHeader = styled.div<CardHeaderProps>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: ${({ center }) => (center ? "center" : "space-between")};

  backdrop-filter: blur(4px);

  background-color: ${({ accentColor }) => !accentColor && colors.white5};
  border-radius: ${({ isEmpty }) =>
    isEmpty ? borderRadius.m : `${borderRadius.m} ${borderRadius.m} 0 0`};

  padding: calc(${space.m} - 2px) ${space.m};

  svg {
    color: ${colors.grey5};
  }

  ${({ accentColor, isEmpty }) =>
    accentColor &&
    !isEmpty &&
    css`
      &::after {
        content: "";

        position: absolute;
        bottom: 0;
        left: 0;

        height: 3px;
        width: 95%;
        background: ${accentColor};
      }
    `}
  @media print {
    padding-left: ${space.xxs};
    page-break-inside: avoid;
    display: block;
    * {
      font-size: 14px !important;
    }
  }
`;

export const CardTitle = styled(Title)`
  flex: 1 1 auto;

  @media print {
    color: ${colors.black};
    font-weight: ${fontStyle.bold};
  }
`;

export const CardContent = styled.div<CardContainerProps>`
  height: 90%; // hack to fit content inside card in modals
  flex: 1 1 auto; // hack to fit content inside card in modals

  border-radius: 0 0 ${borderRadius.m} ${borderRadius.m};
  white-space: pre-wrap;

  ${({ hideContainer }) =>
    hideContainer &&
    css`
      padding: 0 !important;
    `}

  ${({ size }) => {
    switch (size) {
      case "s":
        return css`
          padding: ${space.m};
        `;
      case "m":
        return css`
          padding: ${space.l};
        `;
      default:
        return css`
          padding: ${space.l} ${space.m};

          ${breakpoint("m", "up")} {
            padding: ${space.xl};
          }
        `;
    }
  }}

  @media print {
    padding: 0;
    padding-top: ${space.l};
    height: 100%;
  }
`;

export const StyledActionDropdown = styled.div`
  position: absolute;

  right: ${space.m};
  top: ${space.s};
`;

interface CardTitleItemProps {
  isMuted?: boolean;
}

export const CardTitleItem = styled.span<CardTitleItemProps>`
  color: ${({ isMuted }) => isMuted && colors.grey5};

  a {
    text-decoration: none;
    border-bottom: none !important;
  }
`;
